import { Skeleton } from "@/components/ui/skeleton";

/**
 * Generic page skeleton for most pages
 */
export function PageSkeleton() {
  return (
    <div className="min-h-screen bg-background">
      {/* Navbar skeleton */}
      <div className="h-16 border-b border-border bg-background/95 backdrop-blur">
        <div className="container mx-auto px-4 h-full flex items-center justify-between">
          <Skeleton className="h-8 w-32" />
          <div className="hidden md:flex items-center gap-6">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-20" />
          </div>
          <Skeleton className="h-10 w-28" />
        </div>
      </div>

      {/* Hero section skeleton */}
      <div className="bg-primary py-20">
        <div className="container mx-auto px-4 text-center">
          <Skeleton className="h-6 w-40 mx-auto mb-6 bg-primary-foreground/10" />
          <Skeleton className="h-12 w-3/4 mx-auto mb-4 bg-primary-foreground/10" />
          <Skeleton className="h-12 w-1/2 mx-auto mb-8 bg-primary-foreground/10" />
          <Skeleton className="h-6 w-2/3 mx-auto mb-8 bg-primary-foreground/10" />
          <div className="flex justify-center gap-4">
            <Skeleton className="h-12 w-40 bg-secondary/30" />
            <Skeleton className="h-12 w-32 bg-primary-foreground/10" />
          </div>
        </div>
      </div>

      {/* Content section skeleton */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="p-6 rounded-xl border border-border">
                <Skeleton className="h-12 w-12 rounded-lg mb-4" />
                <Skeleton className="h-6 w-3/4 mb-3" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-5/6" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Document detail page skeleton (apostila, blog detail)
 */
export function DocumentDetailSkeleton() {
  return (
    <div className="min-h-screen bg-background">
      {/* Navbar skeleton */}
      <div className="h-16 border-b border-border bg-background/95 backdrop-blur">
        <div className="container mx-auto px-4 h-full flex items-center justify-between">
          <Skeleton className="h-8 w-32" />
          <div className="hidden md:flex items-center gap-6">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-16" />
          </div>
          <Skeleton className="h-10 w-28" />
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>

      {/* Hero section */}
      <div className="bg-primary py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <Skeleton className="h-16 w-16 rounded-xl bg-primary-foreground/10" />
              <div>
                <Skeleton className="h-10 w-80 mb-2 bg-primary-foreground/10" />
                <Skeleton className="h-5 w-48 bg-primary-foreground/10" />
              </div>
            </div>
            <Skeleton className="h-6 w-full mb-2 bg-primary-foreground/10" />
            <Skeleton className="h-6 w-3/4 mb-8 bg-primary-foreground/10" />
            <div className="flex gap-4">
              <Skeleton className="h-12 w-48 bg-secondary/30" />
              <Skeleton className="h-12 w-36 bg-primary-foreground/10" />
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-6">
            <Skeleton className="h-8 w-64 mb-4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5" />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * List page skeleton (blog list, country list)
 */
export function ListPageSkeleton() {
  return (
    <div className="min-h-screen bg-background">
      {/* Navbar skeleton */}
      <div className="h-16 border-b border-border bg-background/95 backdrop-blur">
        <div className="container mx-auto px-4 h-full flex items-center justify-between">
          <Skeleton className="h-8 w-32" />
          <div className="hidden md:flex items-center gap-6">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-16" />
          </div>
          <Skeleton className="h-10 w-28" />
        </div>
      </div>

      {/* Hero */}
      <div className="bg-primary py-16">
        <div className="container mx-auto px-4 text-center">
          <Skeleton className="h-10 w-64 mx-auto mb-4 bg-primary-foreground/10" />
          <Skeleton className="h-5 w-96 mx-auto bg-primary-foreground/10" />
        </div>
      </div>

      {/* Grid */}
      <div className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="rounded-xl border border-border overflow-hidden">
                <Skeleton className="h-48 w-full" />
                <div className="p-6">
                  <Skeleton className="h-6 w-3/4 mb-3" />
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-5/6" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
